import { SortOrder } from '@/components/ui/table'
import { formatPhoneNumberDisplay, getPhoneEntryMaxDigits } from '@/utils/phone-number'

// Note: only digits and phone punctuation - a name/building query that merely contains a number must not match phones
const PHONE_QUERY_REGEX = /^[\d\s()+\-.]+$/

export interface PersonUser {
  avatar: string | null
  email: string
  firstName: string
  isActive: boolean
  lastName: string
}

export const getPhoneDigits = (phone: string) => phone.replace(/\D/g, '')

const PHONE_LOCAL_DIGITS = 10

// Note: same rule as the clients profile - exactly 10 digits, plus an extension outside production
export const isValidPhoneInput = (phone: string) => {
  const digits = getPhoneDigits(phone)
  return (
    digits.length === PHONE_LOCAL_DIGITS ||
    (digits.length > PHONE_LOCAL_DIGITS && digits.length <= getPhoneEntryMaxDigits())
  )
}

export const getPersonFullName = (user: Pick<PersonUser, 'firstName' | 'lastName'>) =>
  `${user.firstName} ${user.lastName}`.trim()

export const filterBySearch = <T>(
  items: T[],
  search: string | undefined,
  getSearchValues: (item: T) => Array<string | null | undefined>,
  getPhone?: (item: T) => string | number | null | undefined,
) => {
  const query = search?.trim().toLowerCase()
  if (!query) return items

  const isPhoneQuery = PHONE_QUERY_REGEX.test(query)
  const queryDigits = getPhoneDigits(query)

  return items.filter((item) => {
    const phone = getPhone?.(item)
    const matchesPhone =
      isPhoneQuery && !!queryDigits && !!phone && String(phone).includes(queryDigits)

    return (
      matchesPhone ||
      [...getSearchValues(item), formatPhoneNumberDisplay(phone)].some((value) =>
        value?.toLowerCase().includes(query),
      )
    )
  })
}

export const sortItems = <T, K extends string>(
  items: T[],
  sortBy: K | null | undefined,
  sortOrder: SortOrder | null | undefined,
  getSortValue: (item: T, sortBy: K) => string | number,
) => {
  if (!sortBy) return items

  const direction = sortOrder === 'desc' ? -1 : 1

  return [...items].sort((a, b) => {
    const first = getSortValue(a, sortBy)
    const second = getSortValue(b, sortBy)

    if (typeof first === 'string' && typeof second === 'string') {
      return first.localeCompare(second) * direction
    }
    return (Number(first) - Number(second)) * direction
  })
}

export const paginateItems = <T>(items: T[], skip = 0, limit?: number) =>
  limit ? items.slice(skip, skip + limit) : items

// Note: both bounds are inclusive and optional - a missing one leaves that side open
export const filterByDateRange = <T>(
  items: T[],
  fromDate: number | undefined,
  toDate: number | undefined,
  getTimestamp: (item: T) => number,
) => {
  if (!fromDate && !toDate) return items

  return items.filter((item) => {
    const timestamp = getTimestamp(item)
    return (!fromDate || timestamp >= fromDate) && (!toDate || timestamp <= toDate)
  })
}
